import { createTheme } from '@mui/material/styles';
import type {} from '@mui/x-charts/themeAugmentation';

declare module '@mui/material/styles' {
  interface Theme {
    colors: {
      primary: string;
      secondary: string;
      background: {
        primary: string;
        secondary: string;
        navbar: string;
      };
      text: {
        primary: string;
        secondary: string;
      };
      common: {
        white: string;
        softWhite: string;
        black: string;
        softBlack: string;
        bloomfieldGreen: string;
        darkGrey: string;
        lightGrey: string;
        purple: string;
      };
      darkTheme: {
        grey: {
          muted: string;
          light: string;
          medium: string;
          dark: string;
        };
        blue: string;
        red: string;
        green: string;
        yellow: string;
        orange: string;
        purple: string;
        pink: string;
        teal: string;
      };
      status: {
        lightgreen: {
          main: string;
          mainText: string;
          light: string;
          lightText: string;
        };
        red: {
          main: string;
          mainText: string;
          light: string;
          lightText: string;
        };
        orange: {
          main: string;
          mainText: string;
          light: string;
          lightText: string;
        };
        yellow: {
          main: string;
          mainText: string;
          light: string;
          lightText: string;
        };
        green: {
          main: string;
          mainText: string;
          light: string;
          lightText: string;
        };
        blue: {
          main: string;
          mainText: string;
          light: string;
          lightText: string;
        };
        purple: {
          main: string;
          mainText: string;
          light: string;
          lightText: string;
        };
        teal: {
          main: string;
          mainText: string;
          light: string;
          lightText: string;
        };
      };
      blueberryColors: {
        blue: string;
        purple: string;
        pink: string;
        green: string;
        flower: string;
        open: string;
        closed: string;
        petalfall: string;
      };
      highlight: {
        selected: string;
        hover: string;
        mapSelectedPointRing: string;
      };
      palettes: {
        blocks: string[];
        deviceCards: string[];
      };
      canopy: {
        invert: string;
        divider: string;
        primary: {
          main: string;
        };
        content: {
          primary: string;
          secondary: string;
          disabled: string;
          icon: string;
          link: {
            default: string;
            visited: string;
          };
        };
        background: {
          default: string;
          paper: string;
        };
        grey: {
          25: string;
          50: string;
          100: string;
          200: string;
        };
        text: {
          icon: string;
          primary: string;
          secondary: string;
        };
        components: {
          barChart: {
            green: string;
            yellow: string;
            pink: string;
            red: string;
            purple: string;
            blue: string;
            orange: string;
            cyan: string;
          };
        };
      };
    };
    fonts: {
      primary: string;
      secondary: string;
      h1: {
        fontSize: string;
        fontWeight: number;
      };
      h2: {
        fontSize: string;
        fontWeight: number;
      };
      h6: {
        fontSize: string;
        fontWeight: number;
      };
      subtitle1: {
        fontSize: string;
      };
    };
    borderRadius: {
      primary: string;
    };
    recentScansPage: {
      spacing: string;
      card: {
        width: string;
        textPadding: string;
        borderRadius: string;
      };
    };
  }
  interface ThemeOptions {
    colors?: {
      primary?: string;
      secondary?: string;
      background?: {
        primary?: string;
        secondary?: string;
        navbar?: string;
      };
      text?: {
        primary?: string;
        secondary?: string;
      };
      common?: {
        white?: string;
        softWhite?: string;
        black?: string;
        softBlack?: string;
        bloomfieldGreen?: string;
        darkGrey?: string;
        lightGrey?: string;
        purple?: string;
      };
      darkTheme?: {
        grey?: {
          muted?: string;
          light?: string;
          medium?: string;
          dark?: string;
        };
        blue?: string;
        red?: string;
        green?: string;
        yellow?: string;
        orange?: string;
        purple?: string;
        pink?: string;
        teal?: string;
      };
      status?: {
        lightgreen?: {
          main?: string;
          mainText?: string;
          light?: string;
          lightText?: string;
        };
        red?: {
          main?: string;
          mainText?: string;
          light?: string;
          lightText?: string;
        };
        orange?: {
          main?: string;
          mainText?: string;
          light?: string;
          lightText?: string;
        };
        yellow?: {
          main?: string;
          mainText?: string;
          light?: string;
          lightText?: string;
        };
        green?: {
          main?: string;
          mainText?: string;
          light?: string;
          lightText?: string;
        };
        blue?: {
          main?: string;
          mainText?: string;
          light?: string;
          lightText?: string;
        };
        purple?: {
          main?: string;
          mainText?: string;
          light?: string;
          lightText?: string;
        };
        teal?: {
          main?: string;
          mainText?: string;
          light?: string;
          lightText?: string;
        };
      };
      blueberryColors?: {
        blue?: string;
        purple?: string;
        pink?: string;
        green?: string;
        flower?: string;
        open?: string;
        closed?: string;
        petalfall?: string;
      };
      highlight?: {
        selected?: string;
        hover?: string;
        mapSelectedPointRing?: string;
      };
      palettes?: {
        blocks?: string[];
        deviceCards?: string[];
      };
      canopy?: {
        invert?: string;
        divider?: string;
        primary?: {
          main?: string;
        };
        content?: {
          primary?: string;
          secondary?: string;
          disabled?: string;
          icon?: string;
          link?: {
            default?: string;
            visited?: string;
          };
        };
        background?: {
          default?: string;
          paper?: string;
        };
        grey?: {
          25?: string;
          50?: string;
          100?: string;
          200?: string;
        };
        text?: {
          icon?: string;
          primary?: string;
          secondary?: string;
        };
        components?: {
          barChart?: {
            green?: string;
            yellow?: string;
            pink?: string;
            red?: string;
            purple?: string;
            blue?: string;
            orange?: string;
            cyan?: string;
          };
        };
      };
    };
    fonts?: {
      primary?: string;
      secondary?: string;
      h1?: {
        fontSize?: string;
        fontWeight?: number;
      };
      h2?: {
        fontSize?: string;
        fontWeight?: number;
      };
      h6?: {
        fontSize?: string;
        fontWeight?: number;
      };
      subtitle1?: {
        fontSize?: string;
      };
    };
    borderRadius?: {
      primary?: string;
    };
    recentScansPage?: {
      spacing?: string;
      card?: {
        width?: string;
        textPadding?: string;
        borderRadius?: string;
      };
    };
  }
}

// Base theme - USE THESE VARIABLES IN CODE - Sets global Bloomfield variables (sticking as closely as possible to MUI data structure)
const baseTheme = createTheme({
  colors: {
    primary: '#000',
    secondary: '#1976d2', // default primary mui color
    background: {
      primary: '#2b2e32', // darkTheme.grey.medium - copied here for convenience
      secondary: '#1E1E1E',
      // primary: '#F8F8F8',
      // secondary: '#1E1E1E',
      navbar: '#000',
    },
    text: {
      primary: '#fff', // default mui dark theme
      secondary: 'rgba(255, 255, 255, 0.7)', // default mui dark theme
      // secondary: '#7c7e80', // darkTheme.grey.muted - copied here for convenience
      // primary: 'rgba(0, 0, 0, 0.87)',
      // secondary: '#5A5A5A',
    },
    common: {
      white: '#DFDFDF',
      softWhite: '#F8F8F8',
      black: '#000',
      softBlack: '#202020',
      bloomfieldGreen: '#80AF01',
      darkGrey: '#323232',
      lightGrey: '#D9D9D9',
      purple: '#5d3fd3',
      // purple: '#866fde',
    },
    darkTheme: {
      grey: {
        muted: '#7c7e80',
        light: '#393b40',
        medium: '#2b2e32',
        dark: '#1e2125',
      },
      blue: '#1ba1fe',
      red: '#ff354d',
      green: '#01c53a',
      yellow: '#DFB200',
      orange: '#e97d09',
      purple: '#BC58FD',
      pink: '#C58298',
      teal: '#00B6C2',
    },
    status: {
      lightgreen: {
        main: '#5CB975',
        mainText: '#fff',
        light: '#d6f0e0',
        lightText: '#0d6832',
      },
      red: {
        // main: '#FF0000',
        main: '#C62828', // mui - error - dark
        // main: '#FA0303', // mui - error - main
        // main: '#CC0000', // bootstrap
        mainText: '#fff',
        // light: '#ff4444', // bootstrap
        // light: '#EF5350', // mui - error - light
        // lightText: '#fff',
        light: '#f9e1e5', // bootstrap
        lightText: '#af233a', // bootstrap
      },
      orange: {
        main: '#FF9800', // mui - warning - light
        // main: '#ED6C02', // mui - warning - main
        // main: '#FF8800', // bootstrap
        // mainText: '#fff',
        mainText: '#000',
        light: '#fbf0da', // bootstrap
        lightText: '#73510d',
        // light: '#ffbb33', // bootstrap
        // light: '#FFB302',
        // lightText: '#fff',
      },
      yellow: {
        main: '#C8CC00',
        mainText: '#000',
        light: '#fbf0da', // bootstrap
        lightText: '#73510d', // bootstrap
        // light: '#FFE300',
        // lightText: '#fff',
      },
      green: {
        main: '#039F00',
        // main: '#007E33', // bootstrap
        mainText: '#fff',
        // light: '#00C851', // bootstrap
        // lightText: '#fff',
        light: '#d6f0e0', // bootstrap
        lightText: '#0d6832',
      },
      blue: {
        // main: '#01579B', // mui - info - dark
        // main: '#0288D1', // mui - info - main
        main: '#0090F0',
        // main: '#0090F0',
        // main: '#0d47a1', // bootstrap
        mainText: '#fff',
        light: '#dfe7f6', // bootstrap
        lightText: '#2c58a0',
        // light: '#def1f7', // bootstrap
        // lightText: '#1c657d',
        // light: '#4285F4', // bootstrap
        // lightText: '#fff',
        // light: '#03A9F4', // mui - info - light
      },
      purple: {
        // main: '#5D3FD3',
        main: '#9933CC', // bootstrap
        mainText: '#fff',
        light: '#aa66cc', // bootstrap
        lightText: '#fff',
      },
      teal: {
        main: '#00695c', // bootstrap
        mainText: '#fff',
        light: '#2BBBAD', // bootstrap
        lightText: '#fff',
      },
    },
    blueberryColors: {
      blue: '#295BA8',
      purple: '#9250B1',
      pink: '#D085B3',
      green: '#68A434',
      flower: '#CB9F4A',
      closed: '#FFEE93',
      open: '#CB9F4A',
      petalfall: '#B6734E',
    },
    highlight: {
      selected: '#EF5350', // color of selected rowside on map and chart
      hover: '#52b69a', // color of hovered rowside on map and chart
      mapSelectedPointRing: '#ffffff', // color of ring around selected single point on map
    },
    palettes: {
      // block color on map and home page scan card
      // blocks: ['#D0EFF5', '#F0F6D2', '#D1EEDB'],
      // blocks: [
      //   '#d9ed92',
      //   '#b5e48c',
      //   '#99d98c',
      //   '#76c893',
      //   '#52b69a',
      //   '#34a0a4',
      //   '#168aad',
      //   '#1a759f',
      //   '#1e6091',
      //   '#184e77',
      // ],
      blocks: ['#0b2027', '#40798c', '#70a9a1', '#cfd7c7', '#f6f1d1'],
      // blocks: [
      //   '#ffadad',
      //   '#ffd6a5',
      //   '#fdffb6',
      //   '#caffbf',
      //   '#9bf6ff',
      //   '#a0c4ff',
      //   // '#bdb2ff',
      //   // '#ffc6ff',
      //   // '#fffffc',
      // ],
      // blocks: ['#dbcfb0', '#bfc8ad', '#90b494', '#718f94', '#545775'],
      // blocks: ['#bce784', '#5dd39e', '#348aa7', '#525174', '#513b56'],
      // blocks: ['#fe938c', '#e6b89c', '#ead2ac', '#9cafb7', '#4281a4'],
      // blocks: ['#5f634f', '#9bc4cb', '#cfebdf', '#e2fadb', '#dbefbc'],
      // device card image background colors
      // deviceCards: ['#dbcfb0', '#bfc8ad', '#90b494', '#718f94', '#545775'],
      deviceCards: ['#bce784', '#5dd39e', '#348aa7', '#525174', '#513b56'],
      // deviceCards: [
      //   '#1984c5',
      //   '#22a7f0',
      //   '#63bff0',
      //   '#a7d5ed',
      //   '#e2e2e2',
      //   '#e1a692',
      //   '#de6e56',
      //   '#e14b31',
      //   '#c23728',
      // ],
      // deviceCards: ['#D0EFF5', '#F0F6D2', '#D1EEDB'],
      // deviceCards: ['#F000DB', '#621882', '#133DC9', '#0A172D', '#0BC4CF'],
      // deviceCards: ['#5f634f', '#9bc4cb', '#cfebdf', '#e2fadb', '#dbefbc'],
    },
    canopy: {
      invert: '#333333',
      divider: '#333333',
      primary: {
        main: '#82BB41',
      },
      content: {
        primary: '#FFFFFF',
        secondary: '#999999',
        disabled: '#666666',
        icon: '#FFFFFF',
        link: {
          default: '#8FBCFF',
          visited: '#D589F8',
        },
      },
      background: {
        default: '#161616',
        paper: '#1C1C1C',
      },
      grey: {
        25: '#1C1C1C',
        50: '#262626',
        100: '#333333',
        200: '#404040',
      },
      text: {
        primary: '#FFFFFF',
        secondary: '#999999',
        icon: '#FFFFFF',
      },
      components: {
        barChart: {
          green: '#82BB41',
          yellow: '#FFD35E',
          pink: '#FF76CA',
          purple: '#B93AF4',
          blue: '#458FFF',
        },
      },
    },
  },
  fonts: {
    primary: ['Inter', 'Helvetica', 'Roboto', 'Arial', 'sans-serif'].join(','),
    secondary: ['Inter', 'Helvetica', 'Roboto', 'Arial', 'sans-serif'].join(
      ','
    ),
    h1: {
      fontSize: '4rem',
      fontWeight: 300,
    },
    h2: {
      fontSize: '1.8rem',
      fontWeight: 700,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 700,
    },
    subtitle1: {
      fontSize: '0.8rem',
    },
  },
  borderRadius: {
    primary: '0.7rem',
  },
  recentScansPage: {
    spacing: '22px', // space between cards and outer edges
    // spacing: '32px', // space between cards and outer edges
    card: {
      width: '300px',
      textPadding: '12px', // padding around bottom text component
      borderRadius: '10px',
    },
  },
});

// Default theme - Override MUI defaults with Bloomfield defaults
const defaultTheme = createTheme({
  ...baseTheme,
  palette: {
    primary: {
      main: baseTheme.colors.primary,
    },
    secondary: {
      main: baseTheme.colors.secondary,
    },
    background: {
      paper: baseTheme.colors.background.primary,
      default: baseTheme.colors.background.primary,
    },
    text: {
      primary: baseTheme.colors.text.primary,
      secondary: baseTheme.colors.text.secondary,
    },
    common: {
      white: baseTheme.colors.common.white,
      black: baseTheme.colors.common.black,
    },
    info: { main: baseTheme.colors.common.white },
  },
  typography: {
    fontFamily: baseTheme.fonts.primary,
    h1: {
      fontSize: baseTheme.fonts.h1.fontSize,
      fontWeight: baseTheme.fonts.h1.fontWeight,
    },
    h2: {
      fontSize: baseTheme.fonts.h2.fontSize,
      fontWeight: baseTheme.fonts.h2.fontWeight,
    },
    h6: {
      fontFamily: baseTheme.fonts.primary,
      fontSize: baseTheme.fonts.h6.fontSize,
      fontWeight: baseTheme.fonts.h6.fontWeight,
    },
    subtitle1: {
      fontFamily: baseTheme.fonts.secondary,
      fontSize: baseTheme.fonts.subtitle1.fontSize,
    },
    button: {
      fontFamily: baseTheme.fonts.secondary,
      textTransform: 'capitalize',
      color: baseTheme.colors.common.white,
    },
  },
});

// Create custom components. 'styleOverrides' will set the component global defaults. 'variants' can be used to make more specific variants.
const themeComponents = createTheme({
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: defaultTheme.borderRadius.primary,
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '1rem',
          '&:last-child': { padding: '1rem' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderStyle: 'none',
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          '&:last-of-type': { borderRadius: defaultTheme.borderRadius.primary },
          '&:first-of-type': {
            borderRadius: defaultTheme.borderRadius.primary,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          '&.Mui-expanded': {
            minHeight: '10px',
          },
        },
        content: {
          margin: '0.3rem 0',
          '&.Mui-expanded': {
            margin: '1.2rem 0',
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          paddingTop: 0,
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        colorPrimary: {
          '&.MuiSwitch-colorPrimary.Mui-checked': {
            color: defaultTheme.colors.common.bloomfieldGreen,
          },
          '&.MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': {
            backgroundColor: defaultTheme.colors.common.bloomfieldGreen,
          },
          '&.MuiSwitch-colorPrimary .MuiSwitch-track': {
            backgroundColor: defaultTheme.colors.common.bloomfieldGreen,
          },
          '&.MuiSwitch-colorPrimary.Mui-disabled': {
            color: defaultTheme.colors.darkTheme.grey.muted,
            opacity: 0.5,
          },
        },
        disabled: {},
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        colorPrimary: {
          color: defaultTheme.colors.darkTheme.grey.muted, // Unchecked state
          '&.Mui-checked': {
            color: defaultTheme.colors.common.bloomfieldGreen, // Checked state
          },
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        colorPrimary: {
          '&.MuiSlider-thumbColorPrimary': {
            color: defaultTheme.colors.common.bloomfieldGreen,
          },
          '&.MuiSlider-colorPrimary': {
            color: defaultTheme.colors.common.bloomfieldGreen,
          },
          '& .MuiSlider-valueLabel': {
            backgroundColor: defaultTheme.colors.darkTheme.grey.light,
            color: defaultTheme.colors.common.white,
          },
          '& .MuiSlider-track': {
            border: 'none',
            backgroundColor: defaultTheme.colors.common.bloomfieldGreen,
          },
          '& .MuiSlider-rail': {
            backgroundColor: defaultTheme.colors.common.bloomfieldGreen,
          },
          '& .MuiSlider-markLabel': {
            color: defaultTheme.colors.common.white,
            '&.MuiSlider-markLabelActive': {
              opacity: 1,
              color: defaultTheme.colors.common.bloomfieldGreen,
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInput-underline:before': {
            borderBottom: `1px solid ${defaultTheme.colors.darkTheme.grey.light}`, // Default underline color
          },
          '&:hover .MuiInput-underline:before': {
            borderBottom: `1px solid ${defaultTheme.colors.darkTheme.grey.muted}`, // Underline color on hover (for non-focused TextField)
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: defaultTheme.colors.common.white,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: {
          zIndex: 1300, // default is 1500 but this override helps with our custom tooltip popups. Without this, if a select dropdown is used in a popup menu, the menu items appear behind the tooltip so this moves the tooltip to the back.
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          height: '1rem',
          color: defaultTheme.palette.text.secondary,
          '&.Mui-selected, &.Mui-selected:hover': {
            color: defaultTheme.colors.darkTheme.grey.medium,
            backgroundColor: defaultTheme.colors.common.bloomfieldGreen,
          },
          borderColor: defaultTheme.colors.darkTheme.grey.muted,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          // Only way I could target the Mui-DataGrid filter icons when clicking on the 3 dots in the column header
          color: defaultTheme.colors.canopy.content.secondary,
        },
      },
    },
    MuiChartsTooltip: {
      // overriding in global theme since chart tooltip gets rendered on top level instead of inside component
      styleOverrides: {
        mark: {
          borderRadius: '2px',
          height: '0.75rem',
          width: '0.75rem',
          border: 0,
          boxShadow: 'none',
          lineHeight: '1.2425rem',
          margin: '0 0 2px',
        },
        cell: {
          ':nth-last-child(2)': {
            color: defaultTheme.colors.canopy.content.primary,
          },
          ':last-child': {
            color: defaultTheme.colors.canopy.content.secondary,
          },
          fontSize: '0.875rem',
          fontWeight: 400,
          lineHeight: '1.2425rem',
          alignItems: 'center',
        },
        paper: {
          backgroundColor: defaultTheme.colors.canopy.background.default,
        },
      },
    },
  },
});

// Combine all theme objects
const theme = createTheme({
  ...defaultTheme,
  palette: {
    ...defaultTheme.palette,
    mode: 'dark',
  },
  typography: {
    ...defaultTheme.typography,
  },
  components: themeComponents.components,
});

export default theme;
